import {
    WorkflowStepDataConfirm,
    WorkflowStepDetail,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import useConfirmation from '@rhg/hooks/use-confirmation';
import {
    UpdateWorkflowStepParameters,
    useUpdateWorkflowStep,
} from '@rhg/hooks/use-update-workflow-step';
import { WarningOutline } from '@rhg/libs/material/icons';
import { MutateOptions } from '@rhg/libs/react-query';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';

interface Props {
    workflow: WorkflowWorkflowInfo | undefined;
}

export function useWorkflowStepDialog({ workflow }: Props) {
    const { updateWorkflowStep } = useUpdateWorkflowStep();

    const { confirm } = useConfirmation();

    const showConfirmDialog = useCallback(
        async (
            step: WorkflowStepDetail,
            options?: MutateOptions<
                AxiosResponse<WorkflowStepDetail, any>,
                unknown,
                UpdateWorkflowStepParameters,
                unknown
            >
        ) => {
            const data = step.stepDefn?.data as WorkflowStepDataConfirm;
            const confirmChoice = data.choices.find(
                ({ submit }) => submit === 'step_completed'
            );
            const cancelChoice =
                data.choices.find(({ submit }) => submit === 'step_declined') ??
                data.choices.find(({ submit }) => submit === 'step_skipped');

            const submittedStep = (await confirm({
                title: data.title,
                body: data.body,
                trackDialog: 'StepDialogConfirm',
                confirmLabel: confirmChoice?.value,
                cancelLabel: cancelChoice?.value,
                fullScreen: true,
                bodyTextAlign: 'center',
                icon: <WarningOutline color="inherit" />,
                iconColor: 'warning',
            }))
                ? confirmChoice
                : cancelChoice;

            updateWorkflowStep(
                {
                    workflowDefnId: workflow?.workflowDefn.defnId,
                    workflowType: workflow?.workflowDefn.type,
                    stepDefnId: step.stepDefn?.defnId,
                    request: {
                        state: submittedStep?.submit,
                    },
                },
                options
            );
        },
        [
            confirm,
            updateWorkflowStep,
            workflow?.workflowDefn.defnId,
            workflow?.workflowDefn.type,
        ]
    );

    // Temporary options parameter in signature
    const showPollDialog = useCallback(
        async (
            step: WorkflowStepDetail,
            options?: { onSuccess?: (result: boolean) => void }
        ) => {
            const result = await confirm({
                title: `TODO redirect to poll when we implement polls ${step.stepDefn?.defnId}`,
            });
            options?.onSuccess?.(result);
        },
        [confirm]
    );

    return { showConfirmDialog, showPollDialog };
}
